import { Heading } from '@/components/atoms';
import {
  AnalyticsChart,
  MetricCard,
  DataTable,
  AnalyticsEmptyState,
} from '@/components/molecules';
import type { ColumnDef } from '@tanstack/react-table';
import type { Sale, AnalyticsProducts } from '@/types';

interface ProductsTabProps {
  showEmptyChart: boolean;
  showAnalyticsEmptyState: boolean;
  onRowClick: (sale: Sale) => void;
  salesData: Sale[];
  columns: ColumnDef<Sale>[];
  monthlySalesData: Array<{ month: string; sales: number }>;
  emptyChartData: Array<{ month: string; sales: number }>;
  chartConfig: {
    sales: {
      label: string;
      color: string;
    };
  };
  analyticsData?: AnalyticsProducts;
  totalProducts?: number;
  totalStockQuantity?: number;
  mostViewedProductName?: string;
  mostViewedProductCountLabel?: string;
}

export const ProductsTab = ({
  showEmptyChart,
  showAnalyticsEmptyState,
  onRowClick,
  salesData,
  columns,
  monthlySalesData,
  emptyChartData,
  chartConfig,
  analyticsData,
  totalProducts = 0,
  totalStockQuantity = 0,
  mostViewedProductName = '—',
  mostViewedProductCountLabel = '—',
}: ProductsTabProps) => {
  return (
    <div className="space-y-6">
      {/* Product Performance Chart and Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Product Performance Chart */}
        <div className="lg:col-span-2">
          <AnalyticsChart
            title=""
            amount=""
            showEmptyChart={showEmptyChart}
            chartData={monthlySalesData}
            emptyChartData={emptyChartData}
            chartConfig={chartConfig}
            summaryMetrics={[
              {
                title: 'Total Products',
                value: `${totalProducts ?? 0}`,
              },
              {
                title: 'Total Stock Quantity',
                value: `${totalStockQuantity ?? 0}`,
              },
            ]}
          />
        </div>

        {/* Top Product Metrics */}
        <div className="space-y-4">
          <MetricCard
            title="Most Ordered Product"
            value={`${analyticsData?.mostOrderedProduct?.orderCount ?? 0}`}
            subtitle={analyticsData?.mostOrderedProduct?.name ?? '—'}
            icon="trending"
            showEmpty={showAnalyticsEmptyState}
          />
          <MetricCard
            title="Top Revenue Product"
            value={`₦${(analyticsData?.topRevenueProduct?.revenue ?? 0).toLocaleString()}`}
            subtitle={analyticsData?.topRevenueProduct?.name ?? '—'}
            icon="trending"
            showEmpty={showAnalyticsEmptyState}
          />
          <MetricCard
            title="Most Viewed Product"
            value={mostViewedProductCountLabel}
            subtitle={mostViewedProductName}
            icon="trending"
            showEmpty={showAnalyticsEmptyState}
          />
        </div>
      </div>

      {/* Product Performance Table Section */}
      <div className="space-y-4">
        <Heading size="lg">Product Performance Table</Heading>
        {showAnalyticsEmptyState ? (
          <AnalyticsEmptyState message="You don't have any Products" />
        ) : salesData.length === 0 ? (
          <AnalyticsEmptyState message="No product sales to show in this table yet. Sales appear here after customers order your products." />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={salesData}
              onRowClick={onRowClick}
            />
            {/* <div className="flex justify-center mt-4">
              <Button variant="outline" className="text-gray-600">
                View All
              </Button>
            </div> */}
          </>
        )}
      </div>
    </div>
  );
};
